import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, PenTool, Plus, Save, Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { useFeature, upgradeHintFor } from '../lib/features';
import { toast } from '../lib/toast';

function emptyDraft() {
  return { name: '', description: '', voiceTone: '', bestFor: '', systemPrompt: '' };
}

export default function UserRewriterPromptsCard() {
  const unlocked = useFeature('customAiPrompts');
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());

  const promptsQuery = useQuery({
    queryKey: ['user-rewriter-prompts'],
    queryFn: () => api.get('/api/me/rewriter-prompts').then((r) => r.data.prompts),
    enabled: unlocked,
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/api/me/rewriter-prompts', body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-rewriter-prompts'] });
      setAdding(false);
      setDraft(emptyDraft());
      toast.success('Prompt saved');
    },
    onError: (err) => toast.error(err, 'Could not save prompt'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      api.put(`/api/me/rewriter-prompts/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-rewriter-prompts'] });
      setEditingId(null);
      setDraft(emptyDraft());
      toast.success('Prompt updated');
    },
    onError: (err) => toast.error(err, 'Could not update prompt'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/me/rewriter-prompts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-rewriter-prompts'] });
      toast.success('Prompt deleted');
    },
    onError: (err) => toast.error(err, 'Could not delete prompt'),
  });

  function beginEdit(row) {
    setEditingId(row._id);
    setAdding(false);
    setDraft({
      name: row.name,
      description: row.description || '',
      voiceTone: row.voiceTone || '',
      bestFor: row.bestFor || '',
      systemPrompt: row.systemPrompt,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setAdding(false);
    setDraft(emptyDraft());
  }

  function commit() {
    if (!draft.name || draft.systemPrompt.trim().length < 20) {
      toast.error('Name and a system prompt of at least 20 chars are required');
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, body: draft });
    } else {
      createMutation.mutate(draft);
    }
  }

  if (!unlocked) {
    return (
      <div className="card border-dashed border-2 border-gray-200 bg-gray-50/60">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Lock className="w-4 h-4" />
            </span>
            Custom rewriter prompts
            <span className="pill bg-gray-100 text-gray-500">Locked</span>
          </div>
        </div>
        <p className="text-sm text-ink-500">
          Save your own AI rewriter voice/tone presets — Authority Voice, your in-house brand
          tone, a specific client style — and pick one when running a rewrite. Available on{' '}
          {upgradeHintFor('customAiPrompts')}+.
        </p>
      </div>
    );
  }

  const rows = promptsQuery.data || [];

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <PenTool className="w-4 h-4" />
          </span>
          Custom rewriter prompts
          {rows.length > 0 && (
            <span className="text-xs text-ink-400 font-normal ml-1">({rows.length})</span>
          )}
        </div>
        {!adding && !editingId && (
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => {
              setAdding(true);
              setDraft(emptyDraft());
            }}
          >
            <Plus className="w-3 h-3" /> Add prompt
          </button>
        )}
      </div>

      <p className="text-sm text-ink-500 mb-3">
        Save your own voice/tone presets to use when running a rewrite. They appear alongside
        the built-in Booster Pack prompts in the rewriter dropdown.
      </p>

      {(adding || editingId) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-ink-600">
              Name
              <input
                className="input mt-1"
                placeholder="e.g. Brand Voice — Acme"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                maxLength={80}
              />
            </label>
            <label className="text-xs text-ink-600">
              Voice / tone
              <input
                className="input mt-1"
                placeholder="e.g. authoritative-conversational"
                value={draft.voiceTone}
                onChange={(e) => setDraft({ ...draft, voiceTone: e.target.value })}
                maxLength={60}
              />
            </label>
          </div>
          <label className="text-xs text-ink-600 block">
            Description (shown in the picker)
            <input
              className="input mt-1"
              placeholder="When to use this voice"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              maxLength={300}
            />
          </label>
          <label className="text-xs text-ink-600 block">
            Best for (optional)
            <input
              className="input mt-1"
              placeholder="e.g. B2B SaaS marketing pages"
              value={draft.bestFor}
              onChange={(e) => setDraft({ ...draft, bestFor: e.target.value })}
              maxLength={200}
            />
          </label>
          <label className="text-xs text-ink-600 block">
            System prompt
            <textarea
              className="input mt-1 font-mono text-xs"
              rows={8}
              placeholder="You are an expert rewriter…  (minimum 20 characters)"
              value={draft.systemPrompt}
              onChange={(e) => setDraft({ ...draft, systemPrompt: e.target.value })}
              maxLength={6000}
            />
            <span className="text-[10px] text-ink-400">
              {draft.systemPrompt.length} / 6000 characters
            </span>
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost text-xs" onClick={cancelEdit}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={
                !draft.name ||
                draft.systemPrompt.length < 20 ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              onClick={commit}
            >
              <Save className="w-3 h-3" />{' '}
              {editingId
                ? updateMutation.isPending
                  ? 'Saving…'
                  : 'Save changes'
                : createMutation.isPending
                  ? 'Saving…'
                  : 'Create'}
            </button>
          </div>
        </div>
      )}

      {promptsQuery.isLoading ? (
        <p className="text-sm text-ink-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-400">No custom prompts yet. Click "Add prompt" to start.</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {rows.map((p) => (
            <li key={p._id} className="py-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink-800">{p.name}</span>
                  {p.voiceTone && (
                    <span className="pill bg-violet-50 text-violet-700">{p.voiceTone}</span>
                  )}
                  {!p.isActive && <span className="pill bg-gray-100 text-gray-500">Paused</span>}
                </div>
                {p.description && (
                  <div className="text-xs text-ink-500 mt-0.5">{p.description}</div>
                )}
                {p.bestFor && (
                  <div className="text-xs text-ink-400 mt-0.5">
                    <span className="font-semibold">Best for:</span> {p.bestFor}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => beginEdit(p)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs text-rose-600 hover:text-rose-700"
                  onClick={() => {
                    if (window.confirm(`Delete prompt "${p.name}"?`)) {
                      deleteMutation.mutate(p._id);
                    }
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
